import React, { useEffect, useState } from "react";
import Input from "./form/Input";
import FormSelect from "./atoms/FormSelect";
import PlacesAutocomplete from "./form/PlacesAutocomplete";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { emitCustomEvent, useCustomEventListener } from "react-custom-events";
import Company from "../api/Company";
import AddressBook from "../api/AddressBook";
import { toast } from "react-hot-toast";
import { Loader2 } from "lucide-react";

const AddAddressContent = ({ modal = false, companyId = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [company, setCompany] = useState(companyId);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const editMode = location.pathname.search("edit") !== -1;
  const disabled = true;
  const companyOptions = companies.map((_company) => ({
    value: _company.id,
    label: _company.name,
  }));

  useCustomEventListener("placeSelected", (data) => {
    setAddress(data.addressLine);
    setState(data.terms[data.terms.length - 2]?.value);
    setCity(data.terms[data.terms.length - 3]?.value);
    setZip(data.zipCode);
    setLat(data.lat);
    setLng(data.lng);
  });

  const loadCompanies = () => {
    Company.getAll().then((response) => setCompanies(response.data.data));
  };

  const addAddress = () => {
    setLoading(true);
    setValidationErrors(null);

    const param = {
      name: name,
      address: address,
      city: city,
      state: state,
      zip: zip,
      lat: lat,
      lng: lng,
      company_id: company,
    };

    const request = !editMode
      ? AddressBook.add(param)
      : AddressBook.update(params.addressId, param);

    request
      .then((response) => {
        toast.success(
          editMode
            ? t("address-book.address-updated")
            : t("address-book.address-added")
        );

        if (!modal) {
          emitCustomEvent("dataChanged");
          emitCustomEvent("closeModal");
          navigate("/admin/address-book");
        } else {
          emitCustomEvent("addressAdded", response.data.data);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          // Validation error
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  const loadAddress = () => {
    setLoading(true);

    AddressBook.getById(params.addressId)
      .then((response) => {
        response = response.data.data;
        setName(response.name);
        setCompany(response.company_id);
        setAddress(response.address);
        setCity(response.city);
        setState(response.state);
        setZip(response.zip);
        setLat(response.lat);
        setLng(response.lng);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong-please-try-again"));
        }
      })
      .then(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();

    if (editMode) {
      loadAddress();
    }
  }, []);

  return (
    <div className="w-full">
      <div className="bg-white">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addAddress();
          }}
          className="space-y-8"
        >
          {/* Address Information Section */}
          <div>
            <h3 className="text-base font-semibold leading-7 text-gray-900">
              Address Information
            </h3>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Enter the address details below.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
              <div className="col-span-full sm:col-span-1">
                <Input
                  
                  name="name"
                  label={t("common.name")}
                  type="text"
                  value={name}
                  placeholder={t("common.name")}
                  error={
                    validationErrors && validationErrors.name
                      ? validationErrors.name[0]
                      : null
                  }
                  onChange={(e) => setName(e.target.value)}
                  className="w-full"
                />
              </div>

              {!modal && (
                <div className="col-span-full sm:col-span-1">
                  <FormSelect
                    
                    name="company"
                    label={t("locations.assign-a-business")}
                    value={companyOptions.find((o) => o.value === company) || null}
                    placeholder={t("locations.assign-a-business")}
                    options={companyOptions}
                    onChange={(option) => {
                      setCompany(option?.value ?? null);
                    }}
                    darmo={true}
                    disabled={modal}
                  />
                </div>
              )}

              <div className="col-span-full">
                <PlacesAutocomplete
                  
                  label={t("common.address")}
                  placeholder={t("common.address-example")}
                  pValue={address}
                  error={
                    validationErrors && validationErrors.address
                      ? validationErrors.address[0]
                      : null
                  }
                />
              </div>

              {!disabled && (
                <>
                  <div className="sm:col-span-1">
                    <Input
                      
                      name="city"
                      label={t("common.city")}
                      type="text"
                      value={city}
                      placeholder={t("address-book.city-example")}
                      error={
                        validationErrors && validationErrors.city
                          ? validationErrors.city[0]
                          : null
                      }
                      onChange={(e) => setCity(e.target.value)}
                      disabled={true}
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <Input
                      
                      name="state"
                      label={t("common.state")}
                      type="text"
                      value={state}
                      placeholder={t("address-book.state-example")}
                      error={
                        validationErrors && validationErrors.state
                          ? validationErrors.state[0]
                          : null
                      }
                      onChange={(e) => setState(e.target.value)}
                      disabled={true}
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <Input
                      
                      name="zip"
                      label={t("common.zip")}
                      type="text"
                      value={zip}
                      placeholder={"90210"}
                      error={
                        validationErrors && validationErrors.zip
                          ? validationErrors.zip[0]
                          : null
                      }
                      onChange={(e) => setZip(e.target.value)}
                      disabled={true}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="border-t border-gray-900/10">
            <button
              type="button"
              onClick={() => navigate("/admin/address-book")}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t("common.save")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAddressContent;
